import { useCallback, useEffect, useState } from 'react'

import { ArrowDown, Plus } from '@gravity-ui/icons'
import { Button, Spinner, toast } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { reportsTimeApi } from '@/api/reportsTime.api'
import { PageHeader } from '@/components/ui/PageHeader'
import { useTimesheetListStore } from '@/store/timesheetList.store'

import { TimesheetFilters } from './components/TimesheetFilters'
import { TimesheetTable } from './components/TimesheetTable'

export default function TimesheetList() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    reports,
    fetchReports,
    isLoading,
    filters,
    setFilters,
  } = useTimesheetListStore()

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const handleNewReportClick = useCallback(() => {
    navigate('/timesheet/new')
  }, [navigate])

  const handleView = useCallback((id: string) => {
    navigate(`/timesheet/${id}`)
  }, [navigate])

  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = useCallback(async () => {
    setIsDownloading(true)
    const year = filters.year ?? new Date().getFullYear()
    const month = filters.month ?? (new Date().getMonth() + 1)
    try {
      const blob = await reportsTimeApi.generateTimeReport(year, month)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `timesheet-${year}-${String(month).padStart(2, '0')}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download timesheet report:', error)
      toast.danger(t('common.error'), { description: (error as Error).message })
    } finally {
      setIsDownloading(false)
    }
  }, [filters.year, filters.month, t])

  return (
    <div className="p-5 h-full flex flex-col space-y-5">
      <div className="flex justify-between items-start">
        <PageHeader
          title={t('timesheet.title')}
          subtitle={t('timesheet.subtitle')}
        />
        <div className="flex gap-2">
          <Button
            variant="secondary"
            isDisabled={isDownloading}
            onClick={handleDownload}
          >
            {isDownloading ? (
              <Spinner size="sm" color="current" />
            ) : (
              <ArrowDown className="size-4" />
            )}
            {t('common.download')}
          </Button>
          <Button variant="primary" onPress={handleNewReportClick}>
            <Plus />
            {t('timesheet.new_timesheet')}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex gap-5 overflow-hidden min-h-0">
        <TimesheetFilters
          driverID={filters.driverID}
          onDriverChange={id => setFilters({ driverID: id })}
          year={filters.year}
          month={filters.month}
          onPeriodChange={(year, month) => setFilters({ year, month })}
        />

        <div className="flex-1 flex flex-col gap-5 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-4">
              <TimesheetTable
                reports={reports}
                onView={handleView}
              />
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
